import type { QueryError, QueryResponse } from "./request-page.types";

// =============================================================================
// Mutation Executor Input Type
// =============================================================================

export type MutationExecutorInput = {
	pageName: string;
	params: Record<string, string>;
};

// =============================================================================
// Mutation Handle Types (TanStack Query useMutation-inspired)
// =============================================================================

export type MutationHandle = {
	readonly pageName: string;

	/**
	 * Execute the mutation. Returns the response on success, throws on error.
	 * Calls `onSettled` after completion (success or error).
	 *
	 * @param vars - Optional variables for `{{vars.X}}` substitution in the request page.
	 */
	mutateAsync(vars?: Record<string, string>): Promise<QueryResponse>;

	/** Reset the handle (no-op for now, placeholder for future state tracking). */
	reset(): void;
};

export type MutationBuilderOptions = {
	/** The request page name to execute */
	request: string;
	/**
	 * Called after each mutation completes, whether it succeeded or failed.
	 * Use this to invalidate queries (e.g. `myQuery.refetch()`).
	 */
	onSettled?: (data: QueryResponse | undefined, error: QueryError | undefined) => void;
};

export type MutationBuilder = (options: MutationBuilderOptions) => MutationHandle;

// =============================================================================
// Mutation Handle Factory
// =============================================================================

/**
 * Creates a MutationHandle for executing mutations from code cells.
 *
 * Unlike QueryHandle, MutationHandle:
 * - Does not cache results or deduplicate requests
 * - Each `mutateAsync()` call executes independently
 * - Calls `onSettled` after each execution (success or error)
 *
 * @param dispatch - Function that executes the mutation and returns the response
 * @param pageName - The request page name to execute
 * @param onSettled - Optional callback called after each mutation completes
 */
export function createMutationHandle(
	dispatch: (input: MutationExecutorInput) => Promise<QueryResponse>,
	pageName: string,
	onSettled?: (data: QueryResponse | undefined, error: QueryError | undefined) => void
): MutationHandle {
	const handle: MutationHandle = {
		pageName,

		async mutateAsync(vars?: Record<string, string>): Promise<QueryResponse> {
			let data: QueryResponse | undefined;
			let error: QueryError | undefined;

			try {
				data = await dispatch({ pageName, params: vars ?? {} });
				return data;
			} catch (err) {
				// Normalize to QueryError shape
				if (err && typeof err === "object" && "code" in err && "message" in err) {
					error = err as QueryError;
				} else {
					error = {
						code: "UNKNOWN",
						message: err instanceof Error ? err.message : String(err),
					};
				}
				throw error;
			} finally {
				try {
					onSettled?.(data, error);
				} catch {
					// Swallow errors from onSettled to avoid masking the original result
				}
			}
		},

		reset(): void {
			// No-op for now. Placeholder for future state tracking.
		},
	};

	return handle;
}

/**
 * Creates a MutationBuilder function for use in code cells.
 *
 * @param dispatch - Function that executes the mutation and returns the response
 */
export function createMutationBuilder(
	dispatch: (input: MutationExecutorInput) => Promise<QueryResponse>
): MutationBuilder {
	return (options: MutationBuilderOptions) => {
		return createMutationHandle(dispatch, options.request, options.onSettled);
	};
}
